import { axios } from "../utils/axios";
import { _ } from "../utils/lodash";
import { QueryArgs } from "./booking";
import { Payment, Scene } from "./payment";
import { Store } from "./store";

export type CardFindArgs = {
  customer: string;
};

export enum CardStatus {
  PENDING = "pending", // pending payment for the card
  VALID = "valid", // paid gift card before activated
  ACTIVATED = "activated", // paid non-gift card / activated gift card
  EXPIRED = "expired", // expired period, times empty, credit deposit to user
  CANCELED = "canceled" // never used and will never be activated
}

export enum Permission {
  DEVELOP = "develop",
  BOSSBOARD = "bossboard",
  DASHBOARD = "dashboard",
  PLAY_BOOKING = "play booking",
  EVENT_BOOKING = "event booking",
  CARE_BOOKING = "care booking",
  FOOD_BOOKING = "food booking",
  GIFT_BOOKING = "gift booking",
  PARTY_BOOKING = "party booking",
  BOOKING_ALL_STORE = "booking all store",
  BOOKING_CREATE = "booking create",
  BOOKING_CANCEL_REVIEW = "booking cancel review",
  CARD = "card",
  CARD_SELL_STORE = "card sell store",
  CARD_SELL_ALL = "card sell all",
  CUSTOMER = "customer",
  PAYMENT = "payment",
  PAYMENT_DOWNLOAD = "payment download",
  PAYMENT_LAST_WEEK = "payment last week",
  PAYMENT_LAST_MONTH = "payment last month",
  PAYMENT_ALL_DATE = "payment all date",
  CASHIER = "cashier",
  NO_STOCK = "no stock",
  CARD_TYPE = "card-type",
  COUPON = "coupon",
  POST = "post",
  EVENT = "event",
  GIFT = "gift",
  PRODUCT = "product",
  STORE = "store",
  STAFF = "staff",
  ROLE = "role",
  CONFIG = "config"
}

export type PermissionKey = keyof typeof Permission;

export interface Role {
  name: string;
  permissions: Permission[];
}

export interface User {
  role?: Role;
  login?: string;
  password?: string;
  name?: string;
  mobile?: string;
  avatarUrl?: string;
  region?: string;
  country?: string;
  isForeigner?: boolean;
  birthday?: string;
  constellation?: string;
  idCardNo?: string;
  openid?: string;
  store?: Store;
  stores: string[];
  balanceDeposit?: number;
  balanceReward?: number;
  balance: number;
  points?: number;
  cardType?: string;
  cardNo?: string;
  cards: Card[];
  tags: string[];
  can(...permission: PermissionKey[]): boolean;
}

export interface Card {
  customer: User;
  timesLeft: number;
  num?: string;
  status: CardStatus;
  expiresAt: Date;
  expiresAtWas?: Date;
  expiresAtExpected?: Date;
  payments?: Payment[];
  giftCode?: string;
  title: string;
  slug: string;
  type: string;
  isGift: boolean;
  isContract?: boolean;
  adultAsKid?: boolean;
  stores: string[];
  content: string;
  times: number;
  start: Date;
  end: Date;
  balance: number;
  price: number;
  maxKids: number;
  freeParentsPerKid: number;
  scenes: Scene[];
  couponType?: "discount" | "special";
  renew?: "none" | "daily" | "monthly";
  maxTimes?: number;
  overPrice?: number;
  discountPrice?: number;
  discountRate?: number;
  fixedPrice?: number;
  providerData?: Record<string, any>;
  productNos?: string[];
  specialOffer?: string;
  rewardedFromCard?: string;
  rewardedFromBooking?: string;
}

export interface CardType {
  title: string;
  slug: string;
  type: string;
  price: number;
  stores: Store[];
  expiresInDays?: number;
  start: Date;
  end: Date;
  dayType?: "onDaysOnly" | "offDaysOnly" | null;
  enabled: boolean;
  isGift: boolean;
  isContract?: boolean;
  // channel?: CardTypeChannel | null;
  isLimited?: boolean;
  isCombo?: boolean;
  cancelOnExpire: boolean;
  activeOnGiftReceive: boolean;
  oneTimeCheckIn: boolean;
  adultAsKid: boolean;
  offDayMarkupPrice?: number;
  openForClient: boolean;
  openForReception: boolean;
  posterUrl: string;
  posterDenseUrl?: string;
  posterUrls: string[];
  content: string;
  couponSlug?: string;
  customerTags: string[];
  maxPerCustomer?: number;
  quantity?: number;
  rewardCardTypes?: string;
  // rewards: Partial<CardTypeReward>[];
  couponType?: string | null;
  scenes: Scene[];
  paymentStore?: Store;

  // type-related properties below
  times: number;
  balance: number;
  balancePriceGroups: { balance?: number; price?: number }[];
  maxKids: number;
  minKids: number;
  freeParentsPerKid: number;
  overPrice?: number;
  discountPrice?: number;
  discountRate?: number;
  fixedPrice?: number;
  partnerUrl?: string;
}

export interface Coupon {
  title: string;
  slug?: string;
  type: string;
  scene: Scene;
  stores: string[];
  content: string;
  adultsCount: number;
  kidsCount: number;
  hours?: number | null;
  price: number;
  priceThirdParty: number;
  freeParentsPerKid: number;
  start: Date;
  end: Date;
  enabled: true;
  overPrice?: number;
  overQuantity?: number;
  discountPrice?: number;
  discountRate?: number;
  fixedPrice?: number;
  setFoods?: string[];
  productNos?: string[];
  oneTimeCheckIn?: boolean;
  keepTimesOnAmendCard?: boolean;
  adultAsKid?: boolean;
  writeOffAsStoreFlow?: boolean;
  offDayMarkupPrice?: number;
}

export const findUser = ({ keyword }) => axios.request({ url: "/user", params: { keyword } });
export const getUser = ({ id }) => axios.request({ url: `/user/${id}` });
export const updateUser = ({ id, cardNo, idCardNo, isForeigner, country }: { id?: string; cardNo?: string; idCardNo?: string; isForeigner?: boolean; country?: string }) => {
  const data = _.omitBy({ cardNo, idCardNo, isForeigner, country }, _.isNil);

  return axios.request({ method: "put", url: `/user/${id}`, data });
};

export const userDeposit = ({ id, card, paymentGateway, paymentPending, payCode, coupon }) => {
  const data = _.omitBy({ slug: card.slug, customer: id, paymentPending }, _.isNil);
  const params = _.omitBy({ paymentGateway, payCode, coupon }, _.isNil);
  return axios.request({ url: `/card`, method: "post", data, params });
};

export const findUserCards = async (args: CardFindArgs) => {
  const params = _.omitBy(args, _.isNil);
  return axios.request<Card[]>({ url: "/card", params });
};

export const findCardTypes = async () => {
  return axios.request<CardType[]>({ url: "/card-type" });
};

export const findCoupons = async (params: Record<string, any> = {}) => {
  return axios.request<Coupon[]>({ url: "/coupon", params });
};

export const updateCard = async (id: string, data: Partial<Card>) => {
  return axios.request<Card[]>({ url: `/card/${id}`, data, method: "PUT" });
};
